/**
 * Error Boundary Component
 * Feature: 034-global-error-handling
 * Constitution: v1.11.0 - Principle XI (Web Application Architecture)
 * 
 * @description React Error Boundary that catches uncaught JavaScript errors
 * @implements FR-001, FR-002, FR-003, FR-004, FR-020
 * @author Peter Dobler
 * @created 2026-01-20
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import ErrorFallback from './ErrorFallback';
import { ErrorCodes, FrontendErrorContext, ErrorSeverity } from '../../../../shared/types/errors';
import { trackErrorBoundaryError } from '../../services/telemetryService';

/**
 * Props for ErrorBoundary component
 */
export interface ErrorBoundaryProps {
  /** Child components to render */
  children: ReactNode;
  /** Name of this boundary for telemetry */
  name?: string;
  /** Custom fallback component */
  fallback?: ReactNode;
  /** Callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Whether to show the report issue button */
  showReportButton?: boolean;
}

/**
 * State for ErrorBoundary component
 */
export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * React Error Boundary Component
 * FR-001: Wraps application to catch uncaught JavaScript errors
 * FR-002: Displays user-friendly error UI
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Update state when an error is caught
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Log error to telemetry
   * FR-020: Log all errors to central telemetry
   * FR-069: Integrate telemetry with ErrorBoundary
   */
  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Store error info in state
    this.setState({ errorInfo });

    // Build error context for telemetry
    const errorContext: Partial<FrontendErrorContext> = {
      message: error.message,
      code: ErrorCodes.COMPONENT_ERROR,
      timestamp: new Date().toISOString(),
      severity: ErrorSeverity.HIGH,
      component: this.props.name || 'Unknown',
      route: typeof window !== 'undefined' ? window.location.pathname : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    };

    // Log to console in development
    console.error('[ErrorBoundary] Caught error:', {
      error,
      errorInfo,
      context: errorContext,
    });

    // FR-069: Track error in telemetry
    trackErrorBoundaryError(error, this.props.name || 'Unknown', {
      componentStack: errorInfo.componentStack ?? undefined,
    });

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  /**
   * Reset error state (FR-003: "Refresh" recovery action)
   */
  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  override render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, name, showReportButton = true } = this.props;

    if (hasError && error) {
      // Use custom fallback if provided, otherwise use default ErrorFallback
      if (fallback) {
        return fallback;
      }

      return (
        <ErrorFallback
          error={error}
          errorInfo={errorInfo}
          componentName={name}
          onReset={this.handleReset}
          showReportButton={showReportButton}
        />
      );
    }

    return children;
  }
}

export default ErrorBoundary;
